import {
  createRoute,
  formatDifferenceInHoursAndMinutes,
  INFO_NOT_AVAILABLE_NA,
} from '@backoffice-frontend/common'
import { StopNetworkAreaRoutes } from '@backoffice-frontend/sam-common'
import { Text } from '@moia-dev/pace-web'
import { isFuture, isPast, isSameMinute } from 'date-fns'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import type { AdVehicleStreamPassengerCardStopInfoFragment } from './PassengerCard.hook'

const FLEET_API_STOP_ID = 'fleet-api-stop'

export const PickupOrDropOffTime = ({
  isCardDisabled,
  isDropOff = false,
  eta,
  waypointStop,
}: {
  isCardDisabled: boolean
  isDropOff: boolean
  eta: Date
  waypointStop: AdVehicleStreamPassengerCardStopInfoFragment | null
}) => {
  const { t, i18n } = useTranslation()

  const waypointStopLink =
    waypointStop &&
    waypointStop.id !== FLEET_API_STOP_ID &&
    createRoute(StopNetworkAreaRoutes.Show.Stops.Show.route, {
      serviceAreaId: waypointStop.serviceArea.id,
      stopId: waypointStop.id,
    })

  const waypointStopName =
    waypointStop?.names[i18n.language] ?? INFO_NOT_AVAILABLE_NA

  const isEtaInPast = isPast(eta)

  return (
    <Text
      size="md"
      css={theme => ({
        textAlign: 'end',
        color: isCardDisabled
          ? theme.semantic.ColorContentDisabled
          : theme.semantic.ColorContentReduced,
      })}
    >
      {isDropOff ? t('common_dropOff_label') : t('common_pickUp_label')}{' '}
      {waypointStopLink ? (
        <Link
          type="primary"
          target="_blank"
          to={waypointStopLink}
          css={{ color: 'currentcolor', textDecoration: 'none' }}
        >
          <span
            css={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              maxWidth: 230,
            }}
          >
            {waypointStopName}
          </span>
        </Link>
      ) : (
        <span
          css={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: 230,
          }}
        >
          {waypointStopName}
        </span>
      )}{' '}
      {isEtaInPast && t('adFleetMap_planned_label')}{' '}
      <Text
        weight="emphasized"
        css={theme => ({
          color: isCardDisabled
            ? theme.semantic.ColorContentDisabled
            : theme.semantic.ColorContentDefault,
        })}
      >
        {calculatePickupTime(eta, t)}
      </Text>
    </Text>
  )
}

function calculatePickupTime(eta: Date, t: TFunction<'ad-operator-modals'>) {
  if (isSameMinute(eta, Date.now())) return t('adFleetMap_in0Min_label')

  if (isFuture(eta)) {
    return t(`adFleetMap_timeToEtaFuture_label`, {
      eta: formatDifferenceInHoursAndMinutes(eta, new Date(), true),
    })
  }

  return t('adFleetMap_TimeToEtaPast_label', {
    eta: formatDifferenceInHoursAndMinutes(eta, new Date(), true),
  })
}
